<template>
  <div
    id="filters-container"
    class="projects-sidebar-filters"
  >
    <search-projects
      class="sidebar-search"
      v-on="$listeners"
    />

    <div class="filters-heading">
      <i
        class="fas fa-sliders-h"
        aria-hidden="true"
      />
      <span>Filtres</span>
    </div>

    <div
      v-if="activeChips.length"
      class="active-filters"
    >
      <div class="active-filters__header">
        <span>Filtres actifs</span>
        <button
          type="button"
          class="reset-filters"
          @click="resetFilters"
        >
          Réinitialiser les filtres
        </button>
      </div>
      <div class="active-filters__chips">
        <span
          v-for="chip in activeChips"
          :key="chip.key"
          class="filter-chip"
        >
          <span class="filter-chip__text">
            <span class="filter-chip__category">{{ chip.category }}</span>
            <span class="filter-chip__value">{{ chip.label }}</span>
          </span>
          <button
            type="button"
            class="filter-chip__remove"
            :aria-label="`Retirer ${chip.label}`"
            @click="removeChip(chip)"
          >
            ×
          </button>
        </span>
      </div>
    </div>

    <div
      v-if="sidebarFilters.length > 0"
      class="filters-accordions"
    >
      <div
        v-for="filter in sidebarFilters"
        :key="accordionKey(filter)"
        class="filter-accordion"
      >
        <button
          type="button"
          class="filter-accordion__title"
          :aria-expanded="isOpen(filter)"
          @click="toggleAccordion(filter)"
        >
          <span>{{ filterDisplayLabel(filter) }}</span>
          <span
            class="filter-accordion__toggle"
            aria-hidden="true"
          >{{ isOpen(filter) ? '−' : '+' }}</span>
        </button>
        <div
          v-show="isOpen(filter)"
          class="filter-accordion__content"
        >
          <ul class="filter-options">
            <li
              v-for="option in selectableOptions(filter)"
              :key="`${accordionKey(filter)}-${option.value}`"
              class="filter-option"
            >
              <label>
                <input
                  :type="isMultiple(filter) ? 'checkbox' : 'radio'"
                  :name="`filter-${accordionKey(filter)}`"
                  :checked="isOptionSelected(filter, option)"
                  :disabled="loading"
                  @change="onOptionToggle(filter, option, $event.target.checked)"
                >
                <span class="filter-option__label">{{ option.label }}</span>
                <span
                  v-if="option.count != null"
                  class="filter-option__count"
                >{{ option.count }}</span>
              </label>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import axios from '@/axios-client.js';
import SearchProjects from '@/components/Projects/SearchProjects.vue';

const EMPTY_ATTRIBUTE = '__none__';

function normalizeRole(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const ROLE_OPTIONS = [
  { label: 'Lecteur', value: '6', aliases: ['Lecteur', 'lecteur', 'reader'] },
  { label: 'Contributeur', value: '2', aliases: ['Contributeur'] },
  { label: 'Super contributeur', value: '3', aliases: ['Super Contributeur', 'Super contributeur'] },
  { label: 'Modérateur', value: '4', aliases: ['Modérateur'] },
  { label: 'Administrateur', value: '5', aliases: ['Administrateur projet', 'Administrateur'] },
];

export default {
  name: 'ProjectsMenu',

  components: {
    SearchProjects,
  },

  props: {
    loading: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      openAccordions: {},
      classicSelections: {},
      attributesFilter: {},
      facets: {
        moderation: {},
        consultation: {},
        attributes: [],
      },
      facetsLoaded: false,
      hydrated: false,
    };
  },

  computed: {
    ...mapState([
      'user',
      'configuration',
      'projectAttributes',
      'USER_LEVEL_PROJECTS',
    ]),
    ...mapState('projects', [
      'filters',
    ]),

    geoStatus() {
      if (!this.user) return 'anonymous';
      if (this.user.is_superuser) return 'super_admin';
      if (this.user.is_administrator) return 'gestionnaire';
      if (this.user.is_staff) return 'equipe';
      return 'actif';
    },

    canSeeRoles() {
      return ['actif', 'equipe', 'gestionnaire'].includes(this.geoStatus);
    },

    canSeeModeration() {
      return ['equipe', 'gestionnaire', 'super_admin'].includes(this.geoStatus);
    },

    canSeeConsultation() {
      return this.geoStatus !== 'anonymous';
    },

    roleFilter() {
      if (!this.canSeeRoles) return null;
      const levels = this.USER_LEVEL_PROJECTS && typeof this.USER_LEVEL_PROJECTS === 'object'
        ? Object.values(this.USER_LEVEL_PROJECTS)
        : [];
      const counts = {};
      levels.forEach((label) => {
        const role = ROLE_OPTIONS.find(option =>
          option.aliases.some(alias => normalizeRole(alias) === normalizeRole(label))
        );
        if (!role) return;
        counts[role.value] = (counts[role.value] || 0) + 1;
      });
      const options = ROLE_OPTIONS
        .filter(option => counts[option.value])
        .map(option => ({ ...option, count: counts[option.value] }));
      if (!options.length) return null;
      return {
        name: 'user_access_level',
        label: 'Mes rôles',
        multiple: true,
        options,
      };
    },

    moderationFilter() {
      if (!this.canSeeModeration || !this.facetsLoaded) return null;
      const moderated = Number(this.facets.moderation && this.facets.moderation.moderated) || 0;
      const unmoderated = Number(this.facets.moderation && this.facets.moderation.unmoderated) || 0;
      if (moderated < 1 || unmoderated < 1) return null;
      return {
        name: 'moderation',
        label: 'Modération',
        multiple: false,
        options: [
          { label: 'Modéré', value: 'true', count: moderated },
          { label: 'Non modéré', value: 'false', count: unmoderated },
        ],
      };
    },

    consultationFilter() {
      if (!this.canSeeConsultation || !this.facetsLoaded) return null;
      const publicCount = Number(this.facets.consultation && this.facets.consultation.public) || 0;
      const privateCount = Number(this.facets.consultation && this.facets.consultation.private) || 0;
      if (publicCount < 1 || privateCount < 1) return null;
      return {
        name: 'consultation',
        label: 'Consultation',
        multiple: false,
        options: [
          { label: 'Publique', value: 'public', count: publicCount },
          { label: 'Privée', value: 'private', count: privateCount },
        ],
      };
    },

    displayedAttributeFilters() {
      if (!this.facetsLoaded || !this.projectAttributes || !this.projectAttributes.length) return [];
      const byId = {};
      (this.facets.attributes || []).forEach((item) => {
        byId[String(item.id)] = item;
      });
      return this.projectAttributes
        .filter(attribute => attribute.display_filter)
        .map((attribute) => {
          const facet = byId[String(attribute.id)];
          if (!facet) return null;
          const values = facet.values || {};
          const known = new Set();
          const options = this.generateFilterOptions(attribute)
            .map((option) => {
              const count = Number(values[option.value] || values[String(option.value)] || 0);
              known.add(String(option.value));
              return { ...option, count };
            })
            .filter(option => option.count > 0);
          Object.entries(values).forEach(([value, count]) => {
            if (!known.has(String(value)) && Number(count) > 0) {
              options.push({
                filter: attribute.id,
                label: value,
                value,
                count: Number(count),
              });
            }
          });
          if (Number(facet.empty) > 0) {
            options.push({
              filter: attribute.id,
              label: 'Aucun(e)',
              value: EMPTY_ATTRIBUTE,
              count: Number(facet.empty),
            });
          }
          if (!options.length) return null;
          return { ...attribute, options, multiple: true, kind: 'attribute' };
        })
        .filter(Boolean);
    },

    sidebarFilters() {
      return [
        ...this.displayedAttributeFilters,
        ...(this.roleFilter ? [this.roleFilter] : []),
        ...(this.moderationFilter ? [this.moderationFilter] : []),
        ...(this.consultationFilter ? [this.consultationFilter] : []),
      ];
    },

    activeChips() {
      const chips = [];
      this.sidebarFilters.forEach((filter) => {
        this.selectableOptions(filter).forEach((option) => {
          if (!this.isOptionSelected(filter, option)) return;
          chips.push({
            key: `${this.accordionKey(filter)}-${option.value}`,
            category: this.filterDisplayLabel(filter),
            label: option.label,
            filter,
            option,
          });
        });
      });
      return chips;
    },
  },

  watch: {
    'configuration.VUE_APP_DJANGO_API_BASE'(value) {
      if (value && !this.facetsLoaded) {
        this.loadFacets();
      }
    },
    sidebarFilters: {
      immediate: true,
      handler(filters) {
        filters.forEach((filter, index) => {
          const key = this.accordionKey(filter);
          if (this.openAccordions[key] === undefined) {
            this.$set(this.openAccordions, key, index === 0);
          }
        });
      },
    },
    USER_LEVEL_PROJECTS() {
      if (!this.hydrated) return;
      const before = this.classicSelections.user_access_level || null;
      this.reconcileClassic('user_access_level', this.canSeeRoles ? this.roleFilter : null);
      if ((this.classicSelections.user_access_level || null) !== before) {
        this.SET_CURRENT_PAGE(1);
        this.$emit('getData');
      }
    },
  },

  created() {
    this.SET_PROJECTS_FILTER({ filter: 'access_level', value: null });
    this.SET_PROJECTS_FILTER({ filter: 'accessible', value: null });
    if (this.configuration && this.configuration.VUE_APP_DJANGO_API_BASE) {
      this.loadFacets();
    } else {
      this.$emit('getData');
    }
  },

  methods: {
    ...mapMutations('projects', [
      'SET_PROJECTS_FILTER',
      'SET_CURRENT_PAGE',
    ]),

    async loadFacets() {
      const base = this.configuration && this.configuration.VUE_APP_DJANGO_API_BASE;
      if (!base || this.facetsLoaded) return;
      try {
        const response = await axios.get(`${base}v2/projects/facets/`);
        this.facets = response.data || this.facets;
      } catch (err) {
        this.facets = { moderation: {}, consultation: {}, attributes: [] };
      }
      this.facetsLoaded = true;
      this.syncSelectionsFromStore();
      this.$emit('getData');
    },

    syncSelectionsFromStore() {
      if (this.hydrated) return;
      this.hydrated = true;
      ['user_access_level', 'moderation', 'consultation'].forEach((name) => {
        if (this.filters && this.filters[name] != null && this.filters[name] !== '') {
          this.$set(this.classicSelections, name, this.filters[name]);
        }
      });
      if (this.filters && this.filters.attributes) {
        try {
          const parsed = JSON.parse(this.filters.attributes);
          if (parsed && typeof parsed === 'object') {
            this.attributesFilter = parsed;
          }
        } catch (err) {
          this.attributesFilter = {};
        }
      }
      this.displayedAttributeFilters.forEach((filter) => {
        if (!this.attributesFilter[filter.id] && !this.attributesFilter[String(filter.id)]) {
          this.setDefaultFilters(filter);
        }
      });
      this.reconcileClassic('moderation', this.moderationFilter);
      this.reconcileClassic('consultation', this.consultationFilter);
      if (!this.canSeeRoles || !Array.isArray(this.USER_LEVEL_PROJECTS)) {
        this.reconcileClassic('user_access_level', this.canSeeRoles ? this.roleFilter : null);
      }
    },

    reconcileClassic(name, group) {
      const current = this.classicSelections[name];
      if (current == null || current === '') return;
      const allowed = group
        ? new Set(group.options.map(option => String(option.value)))
        : new Set();
      const kept = String(current).split(',').filter(value => allowed.has(value));
      const value = kept.length ? kept.join(',') : null;
      if (value !== String(current)) {
        this.$set(this.classicSelections, name, value);
        this.SET_PROJECTS_FILTER({ filter: name, value });
      }
    },

    generateFilterOptions(filter) {
      if (filter.field_type === 'boolean') {
        return [
          { filter: filter.id, label: 'Afficher', value: 'true' },
        ];
      }
      if (filter.options) {
        return filter.options.map(option => ({
          filter: filter.id || filter.name,
          label: option.name || option.label || option,
          value: option.id || option.value || option,
        }));
      }
      return [];
    },

    filterDisplayLabel(filter) {
      return filter.label || filter.name || '';
    },

    selectableOptions(filter) {
      return (filter.options || []).filter((option) => {
        return option.value !== null && option.value !== undefined && option.value !== '';
      });
    },

    emitUpdatedFilter() {
      const value = Object.keys(this.attributesFilter).length
        ? JSON.stringify(this.attributesFilter)
        : null;
      this.$emit('filter', { filter: 'attributes', value });
    },

    isAttributeFilter(filter) {
      return filter.kind === 'attribute';
    },

    isMultiple(filter) {
      return !!filter.multiple;
    },

    isOptionSelected(filter, option) {
      if (this.isAttributeFilter(filter)) {
        const current = this.attributesFilter[filter.id];
        if (!current) return false;
        return String(current).split(',').includes(String(option.value));
      }
      const selected = this.classicSelections[filter.name];
      if (selected === undefined || selected === null || selected === '') return false;
      if (filter.multiple) {
        return String(selected).split(',').includes(String(option.value));
      }
      return String(selected) === String(option.value);
    },

    onOptionToggle(filter, option, checked) {
      if (this.isAttributeFilter(filter)) {
        this.toggleAttributeOption(filter, option, checked);
        return;
      }
      this.toggleClassicOption(filter, option, checked);
    },

    toggleClassicOption(filter, option, checked) {
      let value;
      if (filter.multiple) {
        let current = this.classicSelections[filter.name]
          ? String(this.classicSelections[filter.name]).split(',').filter(Boolean)
          : [];
        if (checked) {
          current.push(String(option.value));
          current = [...new Set(current)];
        } else {
          current = current.filter(item => item !== String(option.value));
        }
        value = current.length ? current.join(',') : null;
      } else {
        value = checked ? option.value : null;
      }
      this.$set(this.classicSelections, filter.name, value);
      this.$emit('filter', { filter: filter.name, value });
    },

    toggleAttributeOption(filter, option, checked) {
      let current = this.attributesFilter[filter.id]
        ? String(this.attributesFilter[filter.id]).split(',').filter(Boolean)
        : [];
      if (checked) {
        current.push(String(option.value));
        current = [...new Set(current)];
        this.$set(this.attributesFilter, filter.id, current.join(','));
      } else {
        current = current.filter(item => item !== String(option.value));
        if (current.length) {
          this.$set(this.attributesFilter, filter.id, current.join(','));
        } else {
          this.$delete(this.attributesFilter, filter.id);
        }
      }
      this.emitUpdatedFilter();
    },

    updateAttributeFilter({ filter, value, noUpdate }) {
      if (!value) {
        this.$delete(this.attributesFilter, filter);
      } else {
        const current = this.attributesFilter[filter]
          ? String(this.attributesFilter[filter]).split(',').filter(Boolean)
          : [];
        current.push(String(value));
        this.$set(this.attributesFilter, filter, [...new Set(current)].join(','));
      }
      if (noUpdate) {
        const serialized = Object.keys(this.attributesFilter).length
          ? JSON.stringify(this.attributesFilter)
          : null;
        this.SET_PROJECTS_FILTER({ filter: 'attributes', value: serialized });
      } else {
        this.emitUpdatedFilter();
      }
    },

    setDefaultFilters(filter) {
      const defaultFilter = filter.default_filter_enabled ? filter.default_filter_value : null;
      if (!defaultFilter) return;
      defaultFilter.split(',').forEach((defaultValue) => {
        const defaultOption = filter.options.find(option => String(option.value) === String(defaultValue));
        if (defaultOption) {
          this.updateAttributeFilter({ ...defaultOption, noUpdate: true });
        }
      });
    },

    removeChip(chip) {
      this.onOptionToggle(chip.filter, chip.option, false);
    },

    resetFilters() {
      this.classicSelections = {};
      this.attributesFilter = {};
      ['user_access_level', 'moderation', 'consultation', 'attributes', 'access_level'].forEach((name) => {
        this.SET_PROJECTS_FILTER({ filter: name, value: null });
      });
      this.SET_CURRENT_PAGE(1);
      this.$emit('getData');
    },

    accordionKey(filter) {
      if (this.isAttributeFilter(filter)) return `attr-${filter.id}`;
      return filter.name;
    },

    isOpen(filter) {
      return !!this.openAccordions[this.accordionKey(filter)];
    },

    toggleAccordion(filter) {
      const key = this.accordionKey(filter);
      this.$set(this.openAccordions, key, !this.openAccordions[key]);
    },
  }
};
</script>

<style lang="less" scoped>
.projects-sidebar-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.sidebar-search {
  width: 100%;
  flex: none;
}

.filters-heading,
.active-filters {
  flex: none;
}

.filters-accordions {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

@filter-blue: #3A468E;
@filter-text: #4b5563;

.filters-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: @filter-blue;
  font-size: 1.15rem;
  font-weight: 700;
  padding: 0 0.1rem;

  .fas {
    color: @filter-blue;
  }
}

.active-filters__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
  color: @filter-blue;
  font-size: 1rem;
  font-weight: 700;
}

.reset-filters {
  border: 0;
  background: transparent;
  padding: 0;
  color: #2f6fed;
  font-size: 0.72rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: @filter-blue;
  }
}

.active-filters__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  max-width: 100%;
  padding: 0.25rem 0.65rem 0.3rem 0.85rem;
  border: 1px solid @filter-blue;
  border-radius: 999px;
  background: #fff;
  color: @filter-blue;
}

.filter-chip__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
}

.filter-chip__category {
  color: @filter-blue;
  font-size: 0.6rem;
  font-weight: 500;
}

.filter-chip__value {
  color: @filter-blue;
  font-size: 0.95rem;
  font-weight: 700;
}

.filter-chip__remove {
  border: 0;
  background: transparent;
  color: @filter-blue;
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.1rem;
}

.filter-accordion + .filter-accordion {
  margin-top: 0.25rem;
}

.filter-accordion__title {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.1rem;
  border: 0;
  background: transparent;
  color: @filter-blue;
  font-size: 1rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.filter-accordion__toggle {
  color: @filter-blue;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1;
}

.filter-accordion__content {
  padding: 0 0 0.6rem;
}

.filter-options {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.filter-option {
  margin: 0;

  label {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    width: 100%;
    cursor: pointer;
    color: @filter-text;
    font-size: 0.92rem;
    font-weight: 500;
    line-height: 1.35;
  }

  input[type='checkbox'],
  input[type='radio'] {
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    flex: 0 0 auto;
    width: 1.05rem;
    height: 1.05rem;
    margin: 0;
    border: 1.5px solid @filter-blue;
    border-radius: 2px;
    background: #fff;
    cursor: pointer;

    &:checked::after {
      content: '';
      position: absolute;
      left: 0.3rem;
      top: 0.08rem;
      width: 0.3rem;
      height: 0.58rem;
      border: solid #B52222;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    &:focus-visible {
      outline: 2px solid rgba(58, 70, 142, 0.35);
      outline-offset: 1px;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  input[type='radio'] {
    border-radius: 50%;

    &:checked::after {
      left: 50%;
      top: 50%;
      width: 0.48rem;
      height: 0.48rem;
      border: 0;
      border-radius: 50%;
      background: #B52222;
      transform: translate(-50%, -50%);
    }
  }
}

.filter-option__label {
  flex: 1 1 auto;
}

.filter-option__count {
  margin-left: auto;
  color: @filter-text;
  font-weight: 500;
}
</style>
